import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import {
  Warehouse,
  Plus,
  Search,
  Package,
  MapPin,
} from 'lucide-react';
import {
  storageLocationsApi,
  YardStorageLocation,
  StorageLocationType,
  StorageLocationStatus,
  getStorageLocationStatusLabel,
} from '@/api/services/yard.api';
import { locationsApi, Location } from '@/api/services/locations.api';
import { toast } from 'sonner';

export default function YardStorageLocations() {
  const [storageLocations, setStorageLocations] = useState<YardStorageLocation[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<YardStorageLocation | null>(null);

  const [formData, setFormData] = useState({
    locationId: '',
    code: '',
    name: '',
    type: 'RACK' as StorageLocationType,
    zone: '',
    aisle: '',
    rack: '',
    level: '',
    bin: '',
    maxWeight: '',
    maxVolume: '',
  });

  useEffect(() => {
    fetchData();
    fetchLocations();
  }, [search, statusFilter, typeFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await storageLocationsApi.getAll({
        search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter as StorageLocationStatus : undefined,
        type: typeFilter !== 'all' ? typeFilter as StorageLocationType : undefined,
      });
      setStorageLocations(response.data);
    } catch (error) {
      console.error('Error fetching storage locations:', error);
      toast.error('Failed to load storage locations');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationsApi.getAll({});
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleCreate = async () => {
    try {
      await storageLocationsApi.create({
        locationId: formData.locationId,
        code: formData.code,
        name: formData.name,
        type: formData.type,
        zone: formData.zone || undefined,
        aisle: formData.aisle || undefined,
        rack: formData.rack || undefined,
        level: formData.level || undefined,
        bin: formData.bin || undefined,
        maxWeight: formData.maxWeight ? parseFloat(formData.maxWeight) : undefined,
        maxVolume: formData.maxVolume ? parseFloat(formData.maxVolume) : undefined,
      });
      toast.success('Storage location created');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating storage location:', error);
      toast.error('Failed to create storage location');
    }
  };

  const resetForm = () => {
    setFormData({
      locationId: '',
      code: '',
      name: '',
      type: 'RACK',
      zone: '',
      aisle: '',
      rack: '',
      level: '',
      bin: '',
      maxWeight: '',
      maxVolume: '',
    });
  };

  const getStatusVariant = (status: StorageLocationStatus) => {
    switch (status) {
      case 'AVAILABLE': return 'default';
      case 'OCCUPIED': return 'secondary';
      case 'RESERVED': return 'outline';
      case 'MAINTENANCE': return 'destructive';
      case 'BLOCKED': return 'destructive';
      default: return 'secondary';
    }
  };

  const storageTypes: StorageLocationType[] = ['RACK', 'BIN', 'FLOOR', 'OUTDOOR', 'TANK', 'PIPE_RACK', 'CONTAINER'];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Storage Locations</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Manage yard storage locations and bins
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Location
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by code or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="AVAILABLE">Available</SelectItem>
                <SelectItem value="OCCUPIED">Occupied</SelectItem>
                <SelectItem value="RESERVED">Reserved</SelectItem>
                <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                <SelectItem value="BLOCKED">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {storageTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : storageLocations.length === 0 ? (
            <div className="text-center py-12">
              <Warehouse className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No storage locations found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Parent Location</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {storageLocations.map((loc) => (
                  <TableRow key={loc.id}>
                    <TableCell className="font-mono font-medium">{loc.code}</TableCell>
                    <TableCell>{loc.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        {loc.location.name}
                      </div>
                    </TableCell>
                    <TableCell>{loc.type.replace('_', ' ')}</TableCell>
                    <TableCell>{loc.zone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(loc.status)}>
                        {getStorageLocationStatusLabel(loc.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Package className="h-4 w-4 text-slate-400" />
                        {loc._count?.storedItems || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedLocation(loc)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Sheet */}
      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle>Create Storage Location</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label>Parent Location *</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData({ ...formData, locationId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  placeholder="e.g., A-01-03"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  placeholder="Location name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(v) => setFormData({ ...formData, type: v as StorageLocationType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {storageTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Zone</Label>
                <Input
                  placeholder="Zone"
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Aisle</Label>
                <Input
                  placeholder="Aisle"
                  value={formData.aisle}
                  onChange={(e) => setFormData({ ...formData, aisle: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Rack</Label>
                <Input
                  placeholder="Rack"
                  value={formData.rack}
                  onChange={(e) => setFormData({ ...formData, rack: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Level</Label>
                <Input
                  placeholder="Level"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Bin</Label>
                <Input
                  placeholder="Bin"
                  value={formData.bin}
                  onChange={(e) => setFormData({ ...formData, bin: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Weight (kg)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.maxWeight}
                  onChange={(e) => setFormData({ ...formData, maxWeight: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Volume (m³)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.maxVolume}
                  onChange={(e) => setFormData({ ...formData, maxVolume: e.target.value })}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button className="flex-1" onClick={handleCreate}>
                Create Location
              </Button>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* View Details Sheet */}
      <Sheet open={!!selectedLocation} onOpenChange={() => setSelectedLocation(null)}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          {selectedLocation && (
            <>
              <SheetHeader>
                <SheetTitle>{selectedLocation.name}</SheetTitle>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Code</p>
                    <p className="font-mono font-medium">{selectedLocation.code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Type</p>
                    <p>{selectedLocation.type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge variant={getStatusVariant(selectedLocation.status)}>
                      {getStorageLocationStatusLabel(selectedLocation.status)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Zone</p>
                    <p>{selectedLocation.zone || '-'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-2">Parent Location</p>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>{selectedLocation.location.name}</span>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-2">Position</p>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                      <p className="text-slate-500">Aisle</p>
                      <p className="font-medium">{selectedLocation.aisle || '-'}</p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                      <p className="text-slate-500">Rack</p>
                      <p className="font-medium">{selectedLocation.rack || '-'}</p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                      <p className="text-slate-500">Level</p>
                      <p className="font-medium">{selectedLocation.level || '-'}</p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                      <p className="text-slate-500">Bin</p>
                      <p className="font-medium">{selectedLocation.bin || '-'}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-2">Capacity</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                      <p className="text-slate-500">Max Weight</p>
                      <p className="font-medium">
                        {selectedLocation.maxWeight ? `${selectedLocation.maxWeight} kg` : '-'}
                      </p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                      <p className="text-slate-500">Max Volume</p>
                      <p className="font-medium">
                        {selectedLocation.maxVolume ? `${selectedLocation.maxVolume} m³` : '-'}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-2">Stored Items</p>
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-slate-400" />
                    <span className="text-lg font-medium">
                      {selectedLocation._count?.storedItems || 0} items
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
